import { Navigate, Route, Routes, useLocation } from 'react-router-dom';
import { useMemo } from 'react';
import { AuthGuard } from './guards';
import {
  AcceptInviteScreen,
  Error404,
  InventoryDetailScreen,
  LoginScreen,
  SsoScreen,
  OrderDetailScreen,
  OrderListScreen,
  PackingListScreen,
  InboundShipmentDetailRedirect,
  InboundShipmentDetailScreen,
  InboundShipmentListScreen,
  WaveListScreen,
} from './screens';
import { SecureAppLayout } from './layouts';
import { RegisterScreen } from './screens/register';
import { OnboardingScreen } from './screens/app/onboarding';
import { SettingsDetailScreen } from './screens/app/settings';
import { WaveDetailScreen } from './screens/app/fulfill/waves/detail';
import { ExpirationListScreen } from './screens/app/inventory/expirations';
import { InventoryListScreen, ItemLogsScreen } from './screens/app/inventory/items';
import { PickingDetailScreen } from './screens/app/fulfill/packing/id';
import { PickingCompletedDetailScreen } from './screens/app/fulfill/packing/completed-details';
import { TransferDetailScreen } from './screens/app/inventory/transfer';
import { ForgotPasswordPage } from './screens/forgot-password';
import { RequestPasswordPage } from './screens/request-password';
import { UnifiedLoginRedirect } from './screens/unified-login-redirect';
import { ReplenishmentListScreen } from './screens/app/inventory/replenishment';
import { ManifestReportScreen } from './screens/app/reports/manifest';

function App() {
  const { pathname } = useLocation();
  const blockedUrls = ['/app/onboarding', '/app/onboarding/'];

  const hideSidebar = useMemo(() => blockedUrls.includes(pathname), [pathname]);

  return (
    <Routes>
      <Route
        path="/login"
        element={
          <UnifiedLoginRedirect page="login">
            <LoginScreen />
          </UnifiedLoginRedirect>
        }
      />
      <Route path="/sso" element={<SsoScreen />} />
      <Route path="/register" element={<RegisterScreen />} />
      <Route
        path="/forgot-password"
        element={
          <UnifiedLoginRedirect page="forgot-password">
            <ForgotPasswordPage />
          </UnifiedLoginRedirect>
        }
      />
      <Route path="/request-password" element={<RequestPasswordPage />} />
      <Route path="/accept-invite" element={<AcceptInviteScreen />} />
      <Route path="/" exact element={<Navigate to="/login" replace />} />
      <Route
        path="app"
        element={
          <AuthGuard>
            <SecureAppLayout hideSidebar={hideSidebar} />
          </AuthGuard>
        }
      >
        <Route path="onboarding" element={<OnboardingScreen />} />
        <Route path="settings/*" element={<SettingsDetailScreen />} />
        <Route path="inventory">
          <Route path="import/*" element={<InventoryDetailScreen />} />
          <Route path="list" element={<InventoryListScreen />} />
          <Route path="transfer/*" element={<TransferDetailScreen />} />
          <Route path="expirations" element={<ExpirationListScreen />} />
          <Route path="replenishment" element={<ReplenishmentListScreen />} />
          <Route path="item/:id" element={<ItemLogsScreen />} />
        </Route>
        <Route path="shipments">
          <Route index element={<Navigate to="inbound" replace />} />
          <Route path="inbound">
            <Route index element={<InboundShipmentListScreen />} />
            <Route path=":id" element={<InboundShipmentDetailScreen />} />
          </Route>
          <Route path=":id" element={<InboundShipmentDetailRedirect />} />
        </Route>
        <Route path="reports">
          <Route path="manifest" element={<ManifestReportScreen />} />
        </Route>
        <Route path="fulfill">
          <Route path="waves/list/*" element={<WaveListScreen />} />
          <Route path="waves/:id" element={<WaveDetailScreen />} />
          <Route path="orders/details/:id" element={<OrderDetailScreen />} />
          <Route path="orders/*" element={<OrderListScreen />} />
          <Route path="packing/list/*" element={<PackingListScreen />} />
          <Route
            path="packing/details/:containerNumber"
            element={<PickingDetailScreen />}
          />
          <Route
            path="packing/completed/:containerNumber"
            element={<PickingCompletedDetailScreen />}
          />
        </Route>
        <Route index element={<Navigate to="fulfill/orders" replace />} />
      </Route>
      <Route path="*" element={<Error404 />} />
    </Routes>
  );
}

export default App;
